/**
 * Memory Management Settings – Rovo memory options UI (ROVOFE-2071).
 * Renders a section with toggles for past chats and memories, matching the Figma design.
 *
 * @deprecated Use `ManageMemoryContent` from `@atlassian/rovo-memories/ui/manage-memory-content`
 * instead of importing this module. The `./ui/memory-management-settings` export will be removed.
 *
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ChangeEvent, useCallback, useEffect, useState } from 'react';

import { useIntl, FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Link from '@atlaskit/link';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { messages } from './messages';

const styles = cssMap({
	settingRow: {
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		flexWrap: 'nowrap',
		gap: token('space.300'),
	},
	labelDescriptionContainer: {
		flex: '1 1 auto',
		overflowWrap: 'break-word',
	},
	settingTitle: {
		font: token('font.heading.small'),
		color: token('color.text'),
	},
	settingDescription: {
		marginTop: token('space.050'),
		overflow: 'hidden',
		color: token('color.text.subtle'),
		font: token('font.body.small'),
	},
	toggleContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexShrink: 0,
	},
	stack: {
		borderRadius: token('radius.xlarge'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
});

/** @deprecated Prefer {@linkcode ManageMemoryContent} from `@atlassian/rovo-memories/ui/manage-memory-content`. */
export interface MemorySettingToggleProps {
	/** When true, indicates data is being fetched (e.g. initial load) */
	isFetching?: boolean;
	id: string;
	label: string;
	description: React.ReactNode;
	checked: boolean;
	onChange: (checked: boolean) => void | Promise<void>;
	/** Called when the onChange callback succeeds */
	onChangeSuccess?: () => void;
	/** Called when the onChange callback fails */
	onChangeError?: () => void;
}

export const MemorySettingToggle = ({
	isFetching,
	id,
	label,
	description,
	checked,
	onChange,
	onChangeSuccess,
	onChangeError,
}: MemorySettingToggleProps): JSX.Element => {
	const { sendAnalyticsEvent } = useAnalytics();

	// Local state for optimistic toggle updates — reflects the new value immediately
	// while the async onChange runs in the background.
	const [localChecked, setLocalChecked] = useState(checked);

	const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);

	const isLoading = isFetching || isLocalSubmitting;

	// Sync local state when the prop changes (e.g. after server response or external update)
	useEffect(() => {
		setLocalChecked(checked);
	}, [checked]);

	const handleChange = useCallback(
		async (event: ChangeEvent<HTMLInputElement>) => {
			const newChecked = event.target.checked;

			// Optimistically update the toggle immediately
			setLocalChecked(newChecked);
			setIsLocalSubmitting(true);

			sendAnalyticsEvent({
				action: 'change',
				actionSubject: 'toggle',
				actionSubjectId: id,
				attributes: {
					isSelected: newChecked,
				},
			});

			try {
				await onChange(newChecked);
				onChangeSuccess?.();
			} catch {
				// Revert to previous state on failure
				setLocalChecked(checked);
				onChangeError?.();
			} finally {
				setIsLocalSubmitting(false);
			}
		},
		[id, checked, onChange, onChangeSuccess, onChangeError, sendAnalyticsEvent],
	);

	return (
		<Flex xcss={styles.settingRow}>
			<Box xcss={styles.labelDescriptionContainer}>
				<label htmlFor={id}>
					<Box as="span" xcss={styles.settingTitle}>
						{label}
					</Box>
				</label>
				<Box xcss={styles.settingDescription}>
					<Text size="small" color="color.text.subtle">
						{description}
					</Text>
				</Box>
			</Box>
			<Box xcss={styles.toggleContainer}>
				<Toggle
					id={id}
					isChecked={localChecked}
					name={id}
					onChange={handleChange}
					size="large"
					isLoading={isLoading}
					isDisabled={isLoading}
				/>
			</Box>
		</Flex>
	);
};

export interface MemoryManagementSettingsProps {
	allowConversationReference?: boolean;
	allowMemory?: boolean;
	/** When true, indicates data is being fetched (e.g. initial load) */
	isFetching?: boolean;
	learnMoreHref?: string;
	onAllowConversationReferenceChange: (checked: boolean) => void | Promise<void>;
	onAllowMemoryChange: (checked: boolean) => void | Promise<void>;
	onManageMemories: () => void;
	/** Called when a settings update fails */
	onSettingsChangeError?: () => void;
	/** Called when a settings update succeeds */
	onSettingsChangeSuccess?: () => void;
}

const PAST_CHATS_ID = 'allowSearchPastChats';
const SAVE_MEMORIES_ID = 'allowSaveMemories';

export const MemoryManagementSettings = ({
	allowConversationReference = false,
	allowMemory = false,
	isFetching,
	learnMoreHref,
	onAllowConversationReferenceChange,
	onAllowMemoryChange,
	onManageMemories,
	onSettingsChangeError,
	onSettingsChangeSuccess,
}: MemoryManagementSettingsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	// Check feature gate to determine if "past chats" toggle should be visible
	const showPastChatsToggle = FeatureGates.getExperimentValue(
		'rovo_allow_conversation_reference_toggle',
		'isEnabled',
		false,
	);

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'memoryManagementSettings',
			attributes: {
				allowConversationReference,
				allowMemory,
			},
		});
		// Only fire on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sendAnalyticsEvent]);

	return (
		<Box>
			<Stack space="space.200" xcss={styles.stack}>
				{showPastChatsToggle && (
					<MemorySettingToggle
						id={PAST_CHATS_ID}
						label={formatMessage(messages.allowSearchPastChatsLabel)}
						description={formatMessage(messages.allowSearchPastChatsDescription)}
						checked={allowConversationReference}
						onChange={onAllowConversationReferenceChange}
						onChangeSuccess={onSettingsChangeSuccess}
						onChangeError={onSettingsChangeError}
						isFetching={isFetching}
					/>
				)}
				<MemorySettingToggle
					id={SAVE_MEMORIES_ID}
					label={formatMessage(messages.allowSaveMemoriesLabel)}
					description={
						<FormattedMessage
							{...messages.allowSaveMemoriesDescription}
							values={{
								a: (chunks) => (
									<Link
										href="https://support.atlassian.com/rovo/docs/what-is-rovo-memory-management"
										target="_blank"
									>
										{chunks}
									</Link>
								),
							}}
						/>
					}
					checked={allowMemory}
					onChange={onAllowMemoryChange}
					onChangeSuccess={onSettingsChangeSuccess}
					onChangeError={onSettingsChangeError}
					isFetching={isFetching}
				/>
				<Box>
					<Button onClick={onManageMemories} isDisabled={!allowMemory || isFetching}>
						{formatMessage(messages.manageMemoryButton)}
					</Button>
				</Box>
			</Stack>
		</Box>
	);
};

export default MemoryManagementSettings;
